import { Component, computed, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { CropAidAdvice, Treatment, TreatmentType } from '@next/arable-lib/advice';
import { Admission, CropAid } from '@next/arable-lib/crop-aid';
import { AbstractDialogResultData, AbstractFormDialog, FormDialogComponent } from '@next/core-lib/dialog';

import { TreatmentDialogModel } from './treatment-dialog.model';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { AgrNumberDirective } from '@next/core-lib/form';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { NgClass } from '@angular/common';
import { MatList, MatListItem, MatListItemTitle, MatListItemLine } from '@angular/material/list';
import { TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';


@Component({
    selector: 'app-treatment-dialog',
    templateUrl: './treatment-dialog.component.html',
    styleUrls: ['treatment-dialog.component.scss'],
    imports: [
        FormDialogComponent,
        FormsModule,
        ReactiveFormsModule,
        MatIcon,
        MatFormField,
        MatLabel,
        MatInput,
        AgrNumberDirective,
        MatSlideToggle,
        CdkTextareaAutosize,
        NgClass,
        MatList,
        MatListItem,
        MatListItemTitle,
        MatListItemLine,
        TranslatePipe,
        AgrNumberPipe,
    ],
})
export class TreatmentDialogComponent extends AbstractFormDialog<Treatment> {
  data = inject<TreatmentDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<MatDialogRef<TreatmentDialogComponent, AbstractDialogResultData<Treatment>>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);

  formGroup = this.fb.group({
    name: { value: this.data.treatment.name, disabled: true },
    objective: { value: this.data.treatment.objective, disabled: true },
    frequency: { value: this.data.treatment.frequency, disabled: true },
    shouldUseAreaInHectaresManual: {
      value: this.data.treatment.shouldUseAreaInHectaresManual,
      disabled: true,
    },
    areaInHectaresManual: { value: this.data.treatment.areaInHectaresManual, disabled: true },
    moment: { value: this.data.treatment.moment, disabled: true },
    pointOfAttention: { value: this.data.treatment.pointOfAttention, disabled: true },
    driftReduction: { value: this.data.treatment.driftReduction, disabled: true },
    aidAdvices: { value: this.data.treatment.aidAdvices, disabled: true },
  });

  // Computed signal that maps aid advices with their product details and admission numbers
  cropAidAdvices = computed(() => {
    return this.data.treatment.aidAdvices.map((aidAdvice) => {
      const product = this.findProduct(aidAdvice.cropAidId);
      const admissionNumberString = this.getAdmissionNumberString(product?.cropProtection?.admissions);
      return {
        ...product,
        ...aidAdvice,
        admissionNumberString,
      } as CropAidAdvice & { admissionNumberString?: string };
    });
  });

  trackByFn(index: number) {
    return index;
  }

  isGbmTreatment() {
    return String(TreatmentType.GBM) === String(this.data.treatment.treatmentType);
  }

  private getAdmissionNumberString(admissions: Admission[] | undefined): string | undefined {
    if (!admissions || admissions.length < 1) {
      return undefined;
    }

    return `(${admissions.map((admission) => admission.number).join(', ')})`;
  }

  private findProduct(cropAidId: number): CropAid | undefined {
    return this.data.cropAids.find((cropAid) => cropAid.id === cropAidId);
  }
}
